import DOMPurify from 'dompurify';

const ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 'a', 'ul', 'ol', 'li', 'br', 'span', 'p'];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'style', 'data-variable'];

const STYLE_PROP_ALLOWLIST = new Set(['color', 'background-color']);
const COLOR_PATTERN = /^(#(?:[0-9a-fA-F]{3}){1,2}|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)|rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(?:0|1|0?\.\d+)\s*\)|[a-z]+)$/i;

function filterStyle(raw: string): string {
  const decls: string[] = [];
  for (const part of raw.split(';')) {
    const [propRaw, ...valRest] = part.split(':');
    if (!propRaw || valRest.length === 0) continue;
    const prop = propRaw.trim().toLowerCase();
    const val = valRest.join(':').trim();
    if (!STYLE_PROP_ALLOWLIST.has(prop)) continue;
    if (!COLOR_PATTERN.test(val)) continue;
    decls.push(`${prop}:${val}`);
  }
  return decls.join(';');
}

let hooksInstalled = false;
function installHooks() {
  if (hooksInstalled) return;
  hooksInstalled = true;

  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'style') {
      const filtered = filterStyle(data.attrValue);
      if (filtered) {
        data.attrValue = filtered;
      } else {
        data.keepAttr = false;
      }
    }
  });

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      const href = node.getAttribute('href') || '';
      if (/^\s*javascript:/i.test(href) || /^\s*data:/i.test(href)) {
        node.removeAttribute('href');
      }
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

export interface SanitizeOptions {
  /** If true, allow <p> in output. For paragraph-fragments we keep it false. */
  allowParagraph?: boolean;
}

export function sanitizeEmailHtml(dirty: string, opts: SanitizeOptions = {}): string {
  installHooks();
  const tags = opts.allowParagraph ? ALLOWED_TAGS : ALLOWED_TAGS.filter((t) => t !== 'p');
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: tags,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
  }) as unknown as string;
}

/**
 * Converts literal `{{name}}` tokens in raw HTML to chip spans
 * the editor can render as atom nodes. Survives sanitize because
 * `data-variable` is in ALLOWED_ATTR.
 */
export function tokensToChips(html: string): string {
  return html.replace(/\{\{([a-zA-Z][a-zA-Z0-9_]*)\}\}/g, (_m, name) => {
    return `<span data-variable="${name}"></span>`;
  });
}

/**
 * Reverses `tokensToChips`: turns chip spans back into literal `{{name}}`
 * text. Run this on the HTML pulled out of the editor before saving.
 */
export function chipsToTokens(html: string): string {
  return html.replace(
    /<span\b[^>]*\bdata-variable="([a-zA-Z][a-zA-Z0-9_]*)"[^>]*>(?:[^<]|<(?!\/span>))*<\/span>/g,
    (_m, name) => `{{${name}}}`
  );
}
